import { Time } from '@funkey/shared';

/** A tab that was opened against an older deployment asks for asset URLs the current deployment no
 * longer serves. Every shape below is that same deployment skew, and every one of them is cured by
 * loading the page again against the current build:
 *
 * - webpack names the failure `ChunkLoadError`, but only when the import goes through its own
 *   runtime -- a bare `Error` carrying the same message reaches us from nested/async imports, so
 *   the message is matched too;
 * - Turbopack raises `Failed to fetch dynamically imported module` instead of naming the error;
 * - a server action compiled into the old build has no id on the new one, which the server reports
 *   as `Failed to find Server Action`.
 *
 * Deliberately narrow: a generic `Failed to fetch` is far more often a dead API or a flaky network
 * than a stale build, and reloading on those would hide real failures behind a refresh. */
function isDeploymentSkewError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    if (error.name === 'ChunkLoadError') return true;

    return (
        /Loading (CSS )?chunk .+ failed/.test(error.message) ||
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Failed to find Server Action')
    );
}

/** A second failure this soon after a reload means the assets we just fetched are broken as well,
 * so the visitor is not on a stale build and reloading again would only spin. Long enough to cover
 * the reload itself, short enough that a later skew in the same session still recovers. */
const RETRY_WINDOW = Time.ms({ seconds: 30 });

/** Pure half of the recovery, so the loop guard is testable without a browser: the caller supplies
 * the clock and the previous attempt, both of which otherwise live in browser globals. */
export function shouldReloadAfterChunkLoadError(params: {
    error: unknown;
    lastAttemptAt: number | null;
    now: number;
}): boolean {
    if (!isDeploymentSkewError(params.error)) return false;
    if (params.lastAttemptAt === null) return true;

    return params.now - params.lastAttemptAt > RETRY_WINDOW;
}

const LAST_ATTEMPT_STORAGE_KEY = 'funkey.chunk-load-recovery-at';

/** Reloads the current route when `error` is deployment skew, at most once per retry window.
 *
 * The attempt is recorded in `sessionStorage` because it has to outlive the very reload it guards
 * and must not leak into the visitor's next visit. A timestamp rather than a one-shot flag: a flag
 * cleared on successful load needs a hook in the root layout, which is exactly the code that does
 * not run when the failure reaches `global-error.tsx`. */
export function recoverFromChunkLoadError(error: unknown): void {
    const now = Date.now();

    try {
        const stored = Number(window.sessionStorage.getItem(LAST_ATTEMPT_STORAGE_KEY));

        if (
            !shouldReloadAfterChunkLoadError({
                error,
                lastAttemptAt: Number.isFinite(stored) && stored > 0 ? stored : null,
                now,
            })
        ) {
            return;
        }

        window.sessionStorage.setItem(LAST_ATTEMPT_STORAGE_KEY, String(now));
    } catch {
        /* Storage is blocked (private browsing, embedded webview). Without it there is nothing to
         * guard the reload, so leave the visitor on the error screen rather than risk a loop. */
        return;
    }

    window.location.reload();
}
